<template>
  <div
    v-show="value"
    class="s-modal">
    <div
      class="s-modal__backdrop"
      @click="closeModal"
      @keydown.esc="closeModal" />
    <div
      class="s-modal__container">
      <div class="s-modal__wrapper">
        <div
          ref="imageWrapper"
          class="s-img-wrapper">
          <img
            id="image-viewer"
            ref="imageRef"
            :src="url" />
        </div>
        <button
          class="c-button -transparent s-modal__close"
          @click="closeModal">
          <span class="u-colorWhite u-marginRight2 u-hide@tabletMiniDown">
            {{ $t('generic.close') }}
          </span>
          <SvgIcon
            type="close"
            in-white-circle
            larger />
        </button>
        <div
          class="u-marginTop3 o-flex -alignCenter -center"
          @click="closeModal"
          @keydown.esc="closeModal">
          <button
            v-if="showDownloadButton"
            class="s-modal__download c-button -transparent t-noLinkAppearance"
            @click.stop="downloadImage">
            <SvgIcon
              type="download"
              in-white-circle
              larger
              class="u-marginRight2" />
            <span class="u-colorWhite">{{ $t('generic.download') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Panzoom from '@panzoom/panzoom'

export default {
  props: {
    value: Boolean,
    url: {
      type: String,
      required: true,
    },
    filename: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      showDownloadButton: true,
      panzoomInstance: null,
      zoomHandler: null,
    }
  },
  mounted() {
    // Global key handler remains active regardless of modal visibility
    document.addEventListener('keydown', this.handleKeyDown)

    // Initialize Panzoom only if the modal starts visible
    if (this.value) {
      this.$nextTick(this.initPanzoom)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown)

    this.destroyPanzoom()
  },
  watch: {
    value(visible) {
      if (visible) {
        this.$nextTick(this.initPanzoom)
      } else {
        this.destroyPanzoom()
      }
    },
  },
  methods: {
    initPanzoom() {
      // Avoid double-initialization
      if (this.panzoomInstance) return

      this.panzoomInstance = Panzoom(this.$refs.imageRef, {
        pinchAndPan: true,
        maxScale: 4,
        panOnlyWhenZoomed: true,
        minScale: 1,
        // to prevent dragging the image in the bounding area
        contain: 'outside',
      })
      this.$refs.imageRef.parentElement.addEventListener(
        'wheel',
        this.panzoomInstance.zoomWithWheel,
      )
      // Fix image position when zooming and panning then zooming out
      this.zoomHandler = (event) => {
        const MIN_ZOOM_THRESHOLD = 1.0227
        const MOBILE_BREAKPOINT = 576

        if (window.screen.width <= MOBILE_BREAKPOINT) {
        // It can't be 1 since we're using this option contain: 'outside',
        // and it will not make scale return to 1 to prevent dragging outside bounding area, and the lowest value is 1.0226
        // I just increased it a bit, and it won't affect anything, just to make it works
          this.showDownloadButton = event.detail.scale <= MIN_ZOOM_THRESHOLD
          this.$refs.imageWrapper.style.overflow = event.detail.scale >= MIN_ZOOM_THRESHOLD ? 'unset' : 'hidden'
        }
        if (event.detail.scale === 1) {
          this.panzoomInstance.reset()
        }
      }
      this.$refs.imageRef.addEventListener('panzoomzoom', this.zoomHandler)
    },

    destroyPanzoom() {
      if (!this.panzoomInstance) return
      this.$refs.imageRef.parentElement.removeEventListener(
        'wheel',
        this.panzoomInstance.zoomWithWheel,
      )
      this.$refs.imageRef.removeEventListener('panzoomzoom', this.zoomHandler)
      this.panzoomInstance.destroy()
      this.panzoomInstance = null
    },

    closeModal($event) {
      // Just to stop closing the modal if clicked on the image itself
      if ($event.srcElement.id === this.$refs.imageRef.id) {
        return
      }
      this.$emit('input', false)
    },
    async downloadImage(event) {
      if (typeof window.Capacitor !== 'undefined') {
        event.preventDefault()
        window.Capacitor.Plugins.Browser.open({ url: this.url })
        return false
      }
      const image = await fetch(this.url)
      const imageBlog = await image.blob()
      const imageURL = URL.createObjectURL(imageBlog)

      const link = document.createElement('a')
      link.href = imageURL
      link.download = this.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return false
    },
    handleKeyDown(event) {
      // Close if the image modal is open and the user clicked escape.
      if (this.value && event.key === 'Escape') {
        event.stopPropagation()
        event.preventDefault()
        this.closeModal(event)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';
.s-modal {
  @include position(fixed, 0);
  @include z-index('modal');
  align-items: center;
  //background: $color-modal-backdrop;
  cursor: zoom-out;
  display: flex;
  justify-content: center;
  padding: $spacing-3;
}

.s-modal__backdrop {
  background: $color-modal-backdrop;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
.s-modal__container {
  @include size(90vw, 80vh);
  object-fit: contain;
  max-height: max-content;
  max-width: max-content;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.s-modal__wrapper {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  .s-img-wrapper {
    border-radius: 16px;
  }
  img {
    width: 100%;
    height: 100%;
    max-width: 90vw;
    max-height: 80vh;
    object-fit: scale-down;
  }
}
.s-modal__close {
  position: absolute;
  padding: 0;
  top: 10px;
  right: 10px;

  @media #{$mq-tablet-portrait} {
    top: -50px;
    right: 0;
  }
  @media #{$mq-tablet-mini-down} {
    position: fixed;
  }
}
.s-modal__download {
  padding: 0;
}
</style>
